// stores/mainStore.js
import { defineStore } from "pinia";
import {
  getWebsiteHomePageInfo,
  getTabBarConfigList,
  getWebStorePageInfo,
  getActiveDetail,
} from "~/api";
import { getCurrentUserData, getCurrentMallUserData, getCurrencyDataList } from "~/api/mall";

import { get, set } from "lodash-es";

import { mockJwtMap, getPrefixUrl, getCurrentServer } from "@/utils/globalData";
import { logStoreFetchError } from "@/utils/requestDebug";

import config from "@/config/index";
// 创建 Pinia store
export const useMainStore = defineStore("main", {
  state: () => ({
    token: "",
    userInfo: {},
    mallUserInfo: {},
    tokenKeyMap: {},
    storeInfoDataMap: {},
    currencyList: [],
    currentCurrency: {},
    layoutData: {},
    menuData: [],
    menuIdMap: {},
    navigationList: [],
    siteInfo: {},
    hasVisibleNotice: true,
  }),

  actions: {
    // 设置 Token
    setToken(token) {
      this.token = token;
    },

    // 设置用户信息
    setUserInfo(userInfo) {
      this.userInfo = userInfo;
    },

    // 设置菜单数据
    setMenuData(menuData) {
      this.menuData = menuData;
    },

    // 设置菜单 ID Map
    setMenuIdMap(menuIdMap) {
      this.menuIdMap = menuIdMap;
    },
    // 设置 不同店铺的map
    setStoreInfoDataMap(key, val) {
      this.storeInfoDataMap[key] = val;
    },
    // 获取 不同店铺的值
    getStoreInfoDataMap(key) {
      return this.storeInfoDataMap[key];
    },
    // 设置 Token Key Map
    setTokenKeyMap(key, val) {
      this.tokenKeyMap[key] = val;
    },

    // 设置站点信息
    setSiteInfo(data) {
      this.siteInfo = data;
    },
    setHasVisibleNotice(value) {
      this.hasVisibleNotice = !!value;
    },
    getSiteInfo() {
      return this.siteInfo;
    },
    getMallSiteConfig(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      return (
        get(storeInfo, "storePageInfo.mallSiteConfig") ||
        get(storeInfo, "storePageInfo.otherJson.mallSiteConfig") ||
        get(storeInfo, "mallSiteConfig") ||
        {}
      );
    },
    getMallPageConfig(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      
      return get(storeInfo, "storePageInfo.mallPageConfig", []);
    },
    getMatchedMallPageConfig(pageKey, currentServer = getCurrentServer()) {
      const mallPageConfig = this.getMallPageConfig(currentServer);
      const pageConfigList = Array.isArray(mallPageConfig) ? mallPageConfig : [];
      const matchedConfigList = pageConfigList.filter((item) => item?.pageKey == pageKey);

      return matchedConfigList.find((item) => {
        const matchList = Array.isArray(item?.match) ? item.match : [];
        return matchList.some((matchUrl) => {
          return String(matchUrl || "").indexOf(String(currentServer || "")) >= 0;
        });
      }) || null;
    },
    getCommonLayoutConfig(currentServer = getCurrentServer()) {
      const headerMatchConfig = this.getMatchedMallPageConfig("layout-header", currentServer);
      const footerMatchConfig = this.getMatchedMallPageConfig("layout-footer", currentServer);

      return {
        header: {
          ...config.layoutCommonConfig.header,
          // mallPageConfig 命中当前域名时，允许同店铺不同域名切不同 header。
          url: headerMatchConfig?.pageUrl || config.layoutCommonConfig.header.url,
        },
        footer: {
          ...config.layoutCommonConfig.footer,
          // mallPageConfig 命中当前域名时，允许同店铺不同域名切不同 footer。
          url: footerMatchConfig?.pageUrl || config.layoutCommonConfig.footer.url,
        },
      };
    },
    normalizeMallSiteLocale(localeCode) {
      const locale = String(localeCode || "").trim();
      if (!locale) {
        return "";
      }

      const normalizedKey = locale.replace(/_/g, "-").toLowerCase();
      const localeMap = {
        zh: "zh-CN",
        "zh-cn": "zh-CN",
        "zh-hans": "zh-CN",
        "zh-hans-cn": "zh-CN",
        "zh-tw": "zh-TW",
        "zh-hk": "zh-TW",
        "zh-hant": "zh-TW",
        "zh-hant-tw": "zh-TW",
        en: "en",
        "en-us": "en",
        "en-gb": "en",
        vi: "vi_VN",
        "vi-vn": "vi_VN",
        ja: "ja_JP",
        "ja-jp": "ja_JP",
      };

      return localeMap[normalizedKey] || locale;
    },
    getMallSiteDefaultLocale(currentServer = getCurrentServer()) {
      const supportedLocales = ["zh-CN", "zh-TW", "vi_VN", "en", "ja_JP"];
      const defaultLocale = this.normalizeMallSiteLocale(
        get(this.getMallSiteConfig(currentServer), "defaultLocale")
      );

      return supportedLocales.includes(defaultLocale) ? defaultLocale : "";
    },
    getMallSiteDefaultCurrency(currentServer = getCurrentServer()) {
      return String(
        get(this.getMallSiteConfig(currentServer), "defaultCurrency") || ""
      )
        .trim()
        .toUpperCase();
    },

    // 深度设置菜单 ID Map
    deepSetIdMap(menuData, typeMap) {
      menuData.forEach((o) => {
        if (o.id) {
          typeMap[`id_${o.id}`] = o;
        }

        if (o.noticeColumnEntityList && o.noticeColumnEntityList.length) {
          this.deepSetIdMap(o.noticeColumnEntityList, typeMap);
        }
      });
    },

    // 获取用户信息
    async fetchUserInfo() { },

    // 获取 Token
    getTokenByKey(key, headers) {
      return this.tokenKeyMap[key] || mockJwtMap[getPrefixUrl("", headers)];
    },
    // 获取站点菜单
    async fetchSiteMenu($fetch, headers) {
      const currentServer = getCurrentServer(headers);
      const param = {
        pageNo: 0,
        pageSize: 9999,
        showType: "1",
        status: "1",
      };
      try {
        const res = await getTabBarConfigList(param, $fetch, headers);
        if (res.data && res.data.dataList) {
          const list = res.data.dataList.sort((a, b) => {
            const aSort = a.sortNo ?? 0;
            const bSort = b.sortNo ?? 0;
            return aSort - bSort;
          });
          this.navigationList = list;
        }
      } catch (e) {
        logStoreFetchError("fetchSiteMenu", headers, e, { currentServer });
      }

      return this.navigationList;
    },

    // 获取网站店铺信息
    async fetchStoreInfo(currentServer, $fetch, headers) {
      let storeRes = {};

      try {
        storeRes = await getWebStorePageInfo({ currentServer }, $fetch, headers);
      } catch (e) {
        logStoreFetchError("fetchStoreInfo", headers, e, { currentServer });
      }

      if (storeRes?.data) {
        try {
          if (typeof get(storeRes, "data.offiaccountFollowUrl") == "string") {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.offiaccountFollowUrl"))
            );
          }
        } catch (e) {
          console.log(e);
        }
        try {
          if (
            typeof get(storeRes, "data.storePageInfo.otherJson") == "string"
          ) {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.storePageInfo.otherJson"))
            );
          }
        } catch (e) {
          console.log(e);
        }

        this.setStoreInfoDataMap(currentServer, storeRes.data);
        if (process.client) {
          try {
            window.localStorage.setItem("StoreInformation", JSON.stringify(storeRes.data));
          } catch (error) {
            console.log(error);
          }
        }
      }
    },



    // 获取站点信息
    async fetchSiteInfo(currentServer, $fetch, headers) {
      let siteKeyRes = {};

      try {
        siteKeyRes = await getWebsiteHomePageInfo({ currentServer }, $fetch, headers);
      } catch (e) {
        logStoreFetchError("fetchSiteInfo", headers, e, { currentServer });
      }

      if (siteKeyRes?.data) {
        this.setSiteInfo(siteKeyRes.data);
      }
    },

    async fetchLayoutConfig($fetch, headers) {
      let layoutData = {};
      const currentServer = getCurrentServer(headers);
      const commonLayoutConfig = this.getCommonLayoutConfig(currentServer);
      let commonLayouts = [commonLayoutConfig.header.url, commonLayoutConfig.footer.url];
      let currentLayoutUrl = "";
      try {
        for (let i = 0; i < commonLayouts.length; i++) {
          currentLayoutUrl = commonLayouts[i];
          let layoutConfigItemResponse = await getActiveDetail({
            url: commonLayouts[i],
            pageNo: 1,
            pageSize: 1,
          }, $fetch, headers);
          if (get(layoutConfigItemResponse, "data.dataList[0]")) {
            let areaData = get(
              layoutConfigItemResponse,
              "data.dataList[0].areaData"
            );
            let areaDataParseData = null;
            try {
              areaDataParseData = JSON.parse(areaData);
            } catch (e) {
              console.log(e);
            }
            if (areaDataParseData) {
              layoutData[commonLayouts[i]] = areaDataParseData;
            }
          }
        }

        if (process.env.NUXT_BOOTSTRAP_DEBUG === "1") {
          console.info(JSON.stringify({
            type: "nuxt:layout-config-loaded",
            currentServer,
            layoutKeys: Object.keys(layoutData),
          }));
        }
      } catch (e) {
        logStoreFetchError("fetchLayoutConfig", headers, e, {
          currentServer,
          layoutUrl: currentLayoutUrl,
        });
      }
      this.layoutData = layoutData;
    },
    async getCurrentUserData() {
      let appToken = window.localStorage.getItem(config.appTokenKey);
      let mallAppToken = window.localStorage.getItem(config.mallAppTokenKey);
      let currentUserData = {};
      let currentMallUserData = {};
      if (appToken) {
        let currentUserDataRes = await getCurrentUserData({});

        currentUserData = get(currentUserDataRes, "data.dataList[0]", {});
      }
      if (mallAppToken && currentUserData.memberCode) {
        let currentMallUserDataRes = await getCurrentMallUserData({
          param: {
            thirdParty: 5,
            thirdPartyType: "SZT",
            thirdPartyId: currentUserData.memberCode,
          },
        });

        currentMallUserData = get(currentMallUserDataRes, "data", {});
      }
      if (!mallAppToken && currentUserData.memberCode) {
        try {
          const currentMallUserDataRes = await getCurrentMallUserData({
            param: {
              thirdParty: 5,
              thirdPartyType: "SZT",
              thirdPartyId: currentUserData.memberCode,
            },
          });

          currentMallUserData = get(currentMallUserDataRes, "data", currentMallUserData);
        } catch (error) {
          console.log("getCurrentMallUserData fallback error", error);
        }
      }

      this.userInfo = currentUserData;
      this.mallUserInfo = currentMallUserData;

      if (
        !get(this.currentCurrency, "toCurrencyCode") &&
        this.getMallSiteDefaultCurrency()
      ) {
        await this.getCurrencyData();
      }
    },
    saveCurrentCurrency(toCurrency) {
      try {
        if (toCurrency) {


          window.localStorage.setItem('currentCurrency', JSON.stringify(toCurrency));

          this.currentCurrency = toCurrency;
        }
      } catch (e) {
        console.log(e)
      }
    },
    initCurrentCurrency() {
      try {
        let currentCurrencyStr = window.localStorage.getItem('currentCurrency');
        let currentCurrency = JSON.parse(currentCurrencyStr);
        this.currentCurrency = currentCurrency;
      } catch (e) {
        console.log(e)
      }
    },
    async getCurrencyData() {
      if (!process.client) return;
      let appToken = window.localStorage.getItem(config.appTokenKey);
      let currentServer = getCurrentServer();
      if ( this.storeInfoDataMap[currentServer]) {


        let currentUserDataRes = await getCurrencyDataList({
          "param": { "pageNo": 0, "pageSize": 100, "activeFlag": true, "fromCurrencyCode": "CNY", "dealerShopId": this.storeInfoDataMap[currentServer].id, }
        });
        let currencyList = get(currentUserDataRes, 'data.currencyRateDTOList', []);
        const nextCurrencyList = [
          {

            id: "1",
            fromCurrencyCode: "CNY",
            toCurrencyCode: "CNY",
            exchangeRate: 1,
            activeFlag: true,
            deleteFlag: false,

          },
          ...currencyList
        ];
        const seenCurrencyMap = new Set();
        this.currencyList = nextCurrencyList.filter((item) => {
          const toCurrencyCode = String(item?.toCurrencyCode || "").trim().toUpperCase();
          const exchangeRate = String(item?.exchangeRate ?? "").trim();
          const currencyKey = `${toCurrencyCode}__${exchangeRate}`;
          if (!toCurrencyCode || seenCurrencyMap.has(currencyKey)) {
            return false;
          }
          seenCurrencyMap.add(currencyKey);
          return true;
        });
        if (!get(this.currentCurrency, "toCurrencyCode")) {
          const defaultCurrencyCode = this.getMallSiteDefaultCurrency(currentServer);
          const defaultCurrencyItem = this.currencyList.find((item) => {
            return (
              String(item?.toCurrencyCode || "").trim().toUpperCase() ===
              defaultCurrencyCode
            );
          });

          if (defaultCurrencyItem) {
            this.saveCurrentCurrency(defaultCurrencyItem);
          }
        }
        console.log("currentUserDataRes", currentUserDataRes)
      }

    },

    async logout() {
      this.userInfo = {};
      this.mallUserInfo = {};
      removeToken()
      removeMallAppToken();
      if (process.client) {
        window.location.reload();
      }
    }
  },
});
